/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
*/
// Fix: Use relative path for lib/state
import { useUI } from '../lib/state';
import { useState, useEffect } from 'react';
import { useFirestoreBalance } from '../hooks/use-firestore-balance';
import { convertMoneyToCredits } from '../lib/services/razorpay-service';
import { View } from '../App';
import './Header.css';

interface HeaderProps {
  currentView: View;
}

export default function Header({ currentView }: HeaderProps) {
  const { toggleSidebar, isSidebarOpen } = useUI();
  const [isMobile, setIsMobile] = useState(false);
  const { balance, loading: balanceLoading } = useFirestoreBalance();

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 1024);
    };
    
    // Throttle resize events to improve performance
    const throttledCheckMobile = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(checkMobile, 150);
    };
    
    checkMobile(); // Initial check
    window.addEventListener('resize', throttledCheckMobile, { passive: true });
    
    return () => {
      window.removeEventListener('resize', throttledCheckMobile);
      clearTimeout(timeoutId);
    };
  }, []);

  const handleMenuClick = () => {
    toggleSidebar();
  };

  const showMobileButton = isMobile || (typeof window !== 'undefined' && window.innerWidth <= 1024);

  return (
    <header style={{
      padding: '0.375rem 1rem',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      borderRadius: '0.75rem',
      margin: '0.375rem 1rem',
      marginBottom: '0.375rem'
    }}>
      <div className="header-left" style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
        {/* Mobile Menu Button */}
        {showMobileButton && (
          <button
            onClick={handleMenuClick}
            className="mobile-menu-btn"
            style={{
              background: 'transparent',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              borderRadius: '8px',
              width: '36px',
              height: '36px',
              minWidth: '36px',
              minHeight: '36px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              color: '#000000',
              padding: 0,
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
            }}
            aria-label="Toggle menu"
          >
            <span className="material-symbols-outlined" style={{ fontSize: '20px', color: '#000000' }}>
              {isSidebarOpen ? 'close' : 'menu'}
            </span>
          </button>
        )}
        <h1 
          onClick={showMobileButton ? handleMenuClick : undefined}
          style={{
            color: '#000000',
            fontWeight: '700',
            fontSize: '1.125rem',
            margin: 0,
            cursor: showMobileButton ? 'pointer' : 'default',
            lineHeight: '1.4'
          }}
        >vernikaai</h1>
      </div>
      <div className="header-right">
        {/* Balance Section */}
        <div className="header-balance-section">
          <div className="header-balance-header">
            <div className="header-balance-label">
              <span className="material-symbols-outlined">account_balance_wallet</span>
              <span>Credits</span>
            </div>
            <button 
              className={`header-refresh-balance-btn ${balanceLoading ? 'refreshing' : ''}`}
              disabled={balanceLoading}
              title="Refresh credits"
            >
              <span className={`material-symbols-outlined ${balanceLoading ? 'spinning' : ''}`}>
                refresh
              </span>
            </button>
          </div>
          <div className="header-balance-amount">
            {balanceLoading ? (
              <div className="header-balance-loader"></div>
            ) : (
              balance !== null ? convertMoneyToCredits(balance).toLocaleString('en-IN') : '0'
            )}
          </div>
        </div>
      </div>
    </header>
  );
}